import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard as CreditCardIcon, Truck, Shield, ArrowLeft, Package, Eye, EyeOff, MapPin } from 'lucide-react';
import { PaymentForm, CreditCard } from 'react-square-web-payments-sdk';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { createOrder, processPayment, getAddresses } from '../lib/api';
import { toast } from 'sonner';

const ADULT_CATEGORIES = ['cat-adult-health', 'cat-sexual-wellness'];

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [discreetPackaging, setDiscreetPackaging] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);

  const [formData, setFormData] = useState({
    email: user?.email || '',
    firstName: user?.name?.split(' ')[0] || '',
    lastName: user?.name?.split(' ').slice(1).join(' ') || '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
  });

  // Fetch saved addresses if logged in
  useEffect(() => {
    if (user) {
      getAddresses().then(res => {
        setSavedAddresses(res.data || []);
        // Auto-fill default address
        const defaultAddr = res.data?.find(a => a.is_default) || res.data?.[0];
        if (defaultAddr) {
          setFormData(prev => ({
            ...prev,
            email: user.email || prev.email,
            firstName: user.name?.split(' ')[0] || prev.firstName,
            lastName: user.name?.split(' ').slice(1).join(' ') || prev.lastName,
            address: defaultAddr.address,
            city: defaultAddr.city,
            state: defaultAddr.state,
            zipCode: defaultAddr.zipCode,
            country: defaultAddr.country || 'United States',
          }));
        }
      }).catch(() => {});
    }
  }, [user]);

  const hasAdultProducts = useMemo(() => {
    return items.some(item => ADULT_CATEGORIES.includes(item.category_id));
  }, [items]);

  const tax = subtotal * 0.08;
  const shipping = subtotal >= 50 ? 0 : 9.99;
  const total = subtotal + tax + shipping;

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validateForm = useCallback(() => {
    if (!formData.email || !formData.firstName || !formData.lastName ||
        !formData.address || !formData.city || !formData.state || !formData.zipCode) {
      toast.error('Please fill in all required fields');
      return false;
    }
    return true;
  }, [formData]);

  const handleCardTokenized = async (token) => {
    if (!validateForm()) return;
    if (loading) return;

    setLoading(true);
    try {
      // 1. Create the order in DB
      const orderData = {
        items: items.map(item => ({
          product_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.images?.[0],
          category_id: item.category_id,
        })),
        customer_email: formData.email,
        customer_name: `${formData.firstName} ${formData.lastName}`,
        shipping_address: {
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country,
        },
        discreet_packaging: discreetPackaging,
      };

      const orderRes = await createOrder(orderData);
      const orderId = orderRes.data.id;

      // 2. Process payment with Square token
      const paymentRes = await processPayment({
        order_id: orderId,
        source_id: token.token,
        origin_url: window.location.origin,
      });

      if (paymentRes.data.success) {
        clearCart();
        navigate(`/checkout/success?order_id=${orderId}`);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const msg = error.response?.data?.detail || 'Payment failed. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8" data-testid="checkout-page">
      <div className="max-w-[1440px] mx-auto px-4">
        <button
          onClick={() => navigate('/cart')}
          className="flex items-center gap-2 text-slate-600 hover:text-[#003366] mb-6"
          data-testid="back-to-cart-btn"
        >
          <ArrowLeft size={20} />
          Back to Cart
        </button>

        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left column: Form + Payment */}
          <div className="lg:col-span-7 space-y-6">
            {/* Contact */}
            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Contact Information</h2>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                  className="mt-1"
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Shipping */}
            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Shipping Address</h2>

              {/* Saved address selector */}
              {savedAddresses.length > 0 && (
                <div className="mb-4">
                  <Label>Use a saved address</Label>
                  <Select
                    onValueChange={(addrId) => {
                      const addr = savedAddresses.find(a => a.id === addrId);
                      if (addr) {
                        setFormData(prev => ({
                          ...prev,
                          address: addr.address,
                          city: addr.city,
                          state: addr.state,
                          zipCode: addr.zipCode,
                          country: addr.country || 'United States',
                        }));
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1" data-testid="saved-address-select">
                      <SelectValue placeholder="Select a saved address" />
                    </SelectTrigger>
                    <SelectContent>
                      {savedAddresses.map(addr => (
                        <SelectItem key={addr.id} value={addr.id}>
                          <div className="flex items-center gap-2">
                            <MapPin size={14} />
                            <span>{addr.label}: {addr.address}, {addr.city}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required className="mt-1" data-testid="firstname-input" />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required className="mt-1" data-testid="lastname-input" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="address">Street Address *</Label>
                  <Input type="text" id="address" name="address" value={formData.address} onChange={handleChange} required className="mt-1" data-testid="address-input" />
                </div>
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input type="text" id="city" name="city" value={formData.city} onChange={handleChange} required className="mt-1" data-testid="city-input" />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input type="text" id="state" name="state" value={formData.state} onChange={handleChange} required className="mt-1" data-testid="state-input" />
                </div>
                <div>
                  <Label htmlFor="zipCode">ZIP Code *</Label>
                  <Input type="text" id="zipCode" name="zipCode" value={formData.zipCode} onChange={handleChange} required className="mt-1" data-testid="zipcode-input" />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input type="text" id="country" name="country" value={formData.country} onChange={handleChange} className="mt-1" disabled />
                </div>
              </div>
            </div>

            {/* Discreet Packaging */}
            {hasAdultProducts && (
              <div className="bg-white rounded-lg border p-6" data-testid="discreet-packaging-section">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#003366]/10 flex items-center justify-center flex-shrink-0">
                    <Package className="w-6 h-6 text-[#003366]" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-bold text-slate-900 mb-1">Discreet Packaging</h2>
                    <p className="text-sm text-slate-500 mb-4">
                      Your order contains items from our wellness categories. We offer discreet packaging
                      to protect your privacy - plain packaging with no indication of contents.
                    </p>
                    <label
                      className="flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all hover:border-[#003366]/30"
                      style={{
                        borderColor: discreetPackaging ? '#003366' : '#e2e8f0',
                        backgroundColor: discreetPackaging ? 'rgba(0,51,102,0.05)' : 'transparent',
                      }}
                      data-testid="discreet-packaging-label"
                    >
                      <input
                        type="checkbox"
                        checked={discreetPackaging}
                        onChange={(e) => setDiscreetPackaging(e.target.checked)}
                        className="w-5 h-5 rounded border-slate-300 text-[#003366] focus:ring-[#003366]"
                        data-testid="discreet-packaging-checkbox"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {discreetPackaging ? <EyeOff className="w-4 h-4 text-[#003366]" /> : <Eye className="w-4 h-4 text-slate-400" />}
                          <span className="font-medium text-slate-900">
                            {discreetPackaging ? 'Discreet packaging enabled' : 'Enable discreet packaging'}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {discreetPackaging
                            ? 'Your package will arrive in plain, unmarked packaging'
                            : 'Click to ship in plain, unmarked packaging for privacy'}
                        </p>
                      </div>
                      <span className="text-sm font-medium text-[#10B981]">FREE</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Square Payment Form */}
            <div className="bg-white rounded-lg border p-6" data-testid="payment-section">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Payment</h2>
              <PaymentForm
                applicationId={process.env.REACT_APP_SQUARE_APPLICATION_ID}
                locationId={process.env.REACT_APP_SQUARE_LOCATION_ID}
                cardTokenizeResponseReceived={handleCardTokenized}
                createPaymentRequest={() => ({
                  countryCode: 'US',
                  currencyCode: 'USD',
                  total: {
                    amount: total.toFixed(2),
                    label: 'Total',
                  },
                })}
              >
                <CreditCard
                  buttonProps={{
                    css: {
                      backgroundColor: '#003366',
                      fontSize: '16px',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#002855' },
                    },
                    isLoading: loading,
                  }}
                  style={{
                    input: { fontSize: '15px' },
                    'input::placeholder': { color: '#94a3b8' },
                  }}
                />
              </PaymentForm>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-4 justify-center py-4">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Shield size={20} className="text-[#10B981]" />
                Secure Checkout
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CreditCardIcon size={20} className="text-[#003366]" />
                Powered by Square
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Truck size={20} className="text-[#C5A059]" />
                Fast Delivery
              </div>
            </div>
          </div>

          {/* Right column: Order summary */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-lg border p-6 sticky top-24">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Order Summary</h2>

              <div className="space-y-4 max-h-64 overflow-auto mb-4">
                {items.map((item) => (
                  <div key={item.id} className="flex gap-3">
                    <div className="w-16 h-16 rounded bg-slate-100 flex-shrink-0 overflow-hidden">
                      <img src={item.images?.[0] || '/placeholder.png'} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 line-clamp-1">{item.name}</p>
                      <p className="text-sm text-slate-500">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-medium text-slate-900">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>

              <hr className="my-4" />

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="font-medium">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Tax</span>
                  <span className="font-medium">${tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Shipping</span>
                  <span className="font-medium">
                    {shipping === 0 ? <span className="text-[#10B981]">FREE</span> : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                <hr />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-[#003366]">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
